import { useState, useEffect } from 'react';
import { getUsers } from '@/lib/api/users';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  avatar?: string;
}

interface PMSelectorProps {
  value?: number | null;
  onChange: (userId: number) => void;
  error?: string;
}

export const PMSelector = ({ value, onChange, error }: PMSelectorProps) => {
  const [projectManagers, setProjectManagers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    loadProjectManagers();
  }, []);

  const loadProjectManagers = async () => {
    setLoading(true);
    try {
      // PM candidates: pm/admin/owner accounts (owner was missing — CR #97,
      // which is why Erik never appeared in this list)
      const allUsers = await getUsers();
      const pms = allUsers.filter((u: User) =>
        u.role === 'pm' || u.role === 'admin' || u.role === 'owner'
      );
      setProjectManagers(pms);
    } catch (error) {
      console.error('Failed to load project managers:', error);
      setProjectManagers([]);
    } finally {
      setLoading(false);
    }
  };

  const selectedPM = projectManagers.find(pm => pm.id === value);

  const handleSelectPM = (pmId: number) => {
    onChange(pmId);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-900 mb-1">
        Project Manager
      </label>
      
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className={`w-full px-4 py-2 rounded-lg border ${
            error
              ? 'border-error focus:border-error'
              : 'border-gray-200 focus:border-primary'
          } bg-gray-50 text-left focus:outline-none focus:ring-2 focus:ring-primary/20`}
        >
          {selectedPM ? (
            <div className="flex items-center gap-2">
              {selectedPM.avatar ? (
                <img 
                  src={selectedPM.avatar} 
                  alt={selectedPM.username}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">
                    {selectedPM.username.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  {selectedPM.username}
                </div>
                <div className="text-xs text-gray-600">
                  {selectedPM.email}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-gray-600">
              Select a project manager...
            </span>
          )}
        </button>
        <span className="material-symbols-outlined absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none">
          {showDropdown ? 'expand_less' : 'expand_more'}
        </span>
      </div>

      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-600">
              Loading project managers...
            </div>
          ) : projectManagers.length > 0 ? (
            <>
              <button
                onClick={() => handleSelectPM(null as unknown as number)}
                className="w-full text-left px-4 py-3 hover:bg-primary/10 transition-colors border-b border-gray-200"
              >
                <span className="text-gray-600 italic">
                  No project manager assigned
                </span>
              </button>
              {projectManagers.map((pm) => (
                <button
                  key={pm.id}
                  onClick={() => handleSelectPM(pm.id)}
                  className="w-full text-left px-4 py-3 hover:bg-primary/10 transition-colors border-b border-gray-200 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    {pm.avatar ? (
                      <img 
                        src={pm.avatar} 
                        alt={pm.username}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-lg font-bold text-primary">
                          {pm.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {pm.username}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs">email</span>
                        {pm.email}
                      </div>
                      <div className="text-xs text-primary font-medium mt-0.5">
                        {pm.role}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </>
          ) : (
            <div className="p-4 text-center">
              <p className="text-gray-600 mb-2">
                No project managers found
              </p>
              <p className="text-xs text-gray-600">
                Users with PM or Admin role will appear here
              </p>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

